import { Inline } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { BackButton, useGoBack } from 'shared/ui/BackButton';
import styled from 'styled-components';
import { ProfilePageLayout } from '../../ProfilePageLayout';
import { SUPERPAY_WEBVIEW_ROUTES } from '../SuperPayRoute';

const InstantTransferTermsArticle = styled.article`
  max-width: 800px;
  width: 100%;
  padding: 8px 0 80px;

  ul,
  ol {
    margin-bottom: 12px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 6px;
    margin left:16px
  }

  p {
    font-size: 15px;
    line-height: 1.6;
  }
`;

function InstantTransferTermsBody() {
  return (
    <>
      <h1>Instant Transfer Terms &amp; Conditions</h1>

      <p>
        These Instant Transfer Terms &amp; Conditions ("Instant Transfer Terms")
        govern the optional Instant Transfer service (the "Service") offered by
        Super Dispatch, Inc. ("Super Dispatch," "we," "us," or "our"). These
        Instant Transfer Terms supplement and incorporate the Super Dispatch
        Terms of Service (the "Platform Terms"). In the event of any conflict
        between these Instant Transfer Terms and the Platform Terms, these
        Instant Transfer Terms control solely with respect to the Instant
        Transfer Service.
      </p>

      <p>
        By selecting or requesting Instant Transfer for a specific load through
        the Super Dispatch platform, you ("Carrier," "Customer," or "you")
        acknowledge that you have read, understand, and agree to be bound by
        these Instant Transfer Terms.
      </p>

      <h2>1. Nature of the Service</h2>
      <p>
        1.1 Instant Transfer is an optional liquidity feature that allows
        Customer to receive an advance of funds associated with an invoice
        submitted through the Super Dispatch platform.
      </p>
      <p>
        1.2 The Service provides early access to funds that Customer expects to
        receive from the shipper or transportation customer responsible for
        payment for the load ("Shipper").
      </p>
      <p>1.3 Instant Transfer is:</p>
      <ol type="a">
        <li>a discretionary business advance service;</li>
        <li>not a loan or extension of credit;</li>
        <li>not a purchase, assignment, or sale of accounts receivable; and</li>
        <li>not a factoring arrangement.</li>
      </ol>
      <p>
        1.4 Customer retains ownership of all invoices and receivables at all
        times.
      </p>
      <p>
        1.5 The invoice associated with an Instant Transfer is identified as the
        anticipated source of repayment of the Advance Amount but does not
        transfer ownership of the receivable to Super Dispatch.
      </p>

      <h2>2. Eligibility and Approval</h2>
      <p>
        2.1 Access to the Service is subject to eligibility requirements
        established by Super Dispatch in its sole discretion.
      </p>
      <p>2.2 Super Dispatch may consider factors including:</p>
      <ol type="a">
        <li>Customer account history;</li>
        <li>transaction activity;</li>
        <li>payment history of Shippers;</li>
        <li>verification status;</li>
        <li>platform compliance; and</li>
        <li>internal risk management policies.</li>
      </ol>
      <p>
        2.3 Super Dispatch may approve or deny any Instant Transfer request in
        its sole discretion.
      </p>
      <p>
        2.4 Approval of one Instant Transfer request does not guarantee approval
        of future requests.
      </p>

      <h2>3. Advance Amount</h2>
      <p>
        3.1 If a request is approved, Super Dispatch may provide Customer with
        an advance ("Advance Amount") associated with an eligible invoice.
      </p>
      <p>
        3.2 The Advance Amount may represent all or a portion of the invoice
        value.
      </p>
      <p>3.3 Super Dispatch may establish limits relating to:</p>
      <ol type="a">
        <li>transaction size;</li>
        <li>number of advances;</li>
        <li>total outstanding advances; or</li>
        <li>frequency of requests.</li>
      </ol>

      <h2>4. Service Fee</h2>
      <p>
        4.1 Each Instant Transfer is subject to a service fee ("Service Fee").
      </p>
      <p>
        4.2 The Service Fee will be clearly disclosed before Customer accepts
        the transaction.
      </p>
      <p>4.3 The Service Fee is charged in consideration for:</p>
      <ol type="a">
        <li>providing early access to funds;</li>
        <li>facilitating payment processing; and</li>
        <li>
          assuming administrative and operational risk associated with payment
          timing.
        </li>
      </ol>
      <p>4.4 The Service Fee:</p>
      <ol type="a">
        <li>is earned when the Advance Amount is funded;</li>
        <li>is not interest; and</li>
        <li>
          is not calculated based on the time the advance remains outstanding.
        </li>
      </ol>

      <h2>5. Repayment Through Shipper Payment</h2>
      <p>
        5.1 Customer authorizes Super Dispatch to collect payment of the
        applicable invoice directly from the Shipper through the Super Dispatch
        platform or associated payment systems.
      </p>
      <p>
        5.2 If Super Dispatch receives payment from the Shipper for the invoice
        associated with an Instant Transfer, Super Dispatch will retain from
        that payment an amount equal to:
      </p>
      <ol type="a">
        <li>the Advance Amount; and</li>
        <li>the Service Fee.</li>
      </ol>
      <p>5.3 Any remaining balance will be transmitted to Customer.</p>

      <h2>6. Shipper Non-Payment; Customer Repayment Obligation</h2>
      <p>
        6.1 If the Shipper does not pay the invoice associated with an Instant
        Transfer for any reason, including dispute, delay, refusal to pay,
        insolvency, chargeback, or other payment failure, Customer agrees to
        repay Super Dispatch the Advance Amount and the Service Fee
        (collectively, the "Repayment Amount").
      </p>
      <p>
        6.2 Super Dispatch will notify Customer if a Shipper fails to pay an
        invoice associated with an Instant Transfer. Customer agrees to repay
        the Repayment Amount within ten (10) days after receiving notice from
        Super Dispatch.
      </p>
      <p>
        6.3 Customer authorizes Super Dispatch to recover the Repayment Amount
        using one or more of the following methods:
      </p>
      <ol type="a">
        <li>
          retaining amounts later received from the Shipper associated with the
          invoice;
        </li>
        <li>
          offsetting the Repayment Amount against any funds otherwise payable to
          Customer through the Super Dispatch platform;
        </li>
        <li>
          applying funds held in Customer's Super Dispatch wallet or similar
          account balances;
        </li>
        <li>
          applying payments received for other loads or invoices payable to
          Customer through the platform; or
        </li>
        <li>
          initiating electronic debit entries to Customer's designated bank
          account(s).
        </li>
      </ol>
      <p>
        6.4 Customer authorizes Super Dispatch and its payment service
        providers, including any third-party payment processors used by Super
        Dispatch, to initiate electronic debit entries (including ACH debits) to
        any bank account linked to Customer's Super Dispatch account to collect
        amounts owed under these Instant Transfer Terms.
      </p>
      <p>
        6.5 Customer represents that any bank account provided to Super Dispatch
        is a business account authorized for ACH debit transactions and that
        Customer has authority to authorize such debits.
      </p>
      <p>
        6.6 If the Repayment Amount is not recovered through the methods
        described above, Super Dispatch may pursue any other lawful means of
        recovery available under the Platform Terms or applicable law.
      </p>

      <h2>7. Customer Representations</h2>
      <p>
        Customer represents and warrants that, at the time of each Instant
        Transfer request:
      </p>
      <ol type="a">
        <li>the invoice reflects completed transportation services;</li>
        <li>the invoice is valid and accurate;</li>
        <li>Customer is entitled to payment from the Shipper;</li>
        <li>Customer is not aware of any dispute regarding the invoice; and</li>
        <li>
          the invoice has not been previously assigned, pledged, or otherwise
          encumbered.
        </li>
      </ol>

      <h2>8. Customer Obligations</h2>
      <p>Customer agrees to:</p>
      <ol type="a">
        <li>provide accurate documentation relating to each invoice;</li>
        <li>promptly notify Super Dispatch of any dispute with a Shipper;</li>
        <li>cooperate with verification requests from Super Dispatch; and</li>
        <li>
          avoid directing Shipper payments away from the Super Dispatch platform
          for invoices subject to Instant Transfer.
        </li>
      </ol>

      <h2>9. Third-Party Payment Processing</h2>
      <p>
        Payments under the Service may be facilitated through third-party
        payment infrastructure providers integrated into the Super Dispatch
        platform.
      </p>
      <p>
        Customer agrees to comply with any applicable third-party payment
        service terms.
      </p>

      <h2>10. Service Limits and Suspension</h2>
      <p>
        Super Dispatch may limit, suspend, or terminate access to Instant
        Transfer, impose transaction caps, adjust pricing, or modify eligibility
        criteria at any time for risk management, compliance, or operational
        reasons.
      </p>

      <h2>11. No Guarantee of Shipper Payment</h2>
      <p>Super Dispatch does not guarantee that Shippers will pay invoices.</p>

      <h2>12. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Super Dispatch shall not be
        liable for Shipper payment disputes, Shipper insolvency, delayed
        payments, or other events outside Super Dispatch's reasonable control.
      </p>

      <h2>13. Governing Law and Dispute Resolution</h2>
      <p>
        These Instant Transfer Terms are governed by the laws specified in the
        Super Dispatch Terms of Service. Any disputes relating to the Service
        will be resolved in accordance with the dispute resolution provisions
        contained in the Platform Terms.
      </p>
    </>
  );
}

function InstantTransferTermsWebviewBackRow() {
  const goBack = useGoBack(SUPERPAY_WEBVIEW_ROUTES.promo);

  return (
    <Inline verticalAlign="center" space="small">
      <BackButton onClick={goBack} label="Go back to previous page" />
    </Inline>
  );
}

export function InstantTransferTermsContentPage() {
  return (
    <>
      <DocumentTitle title="Instant Transfer Terms & Conditions" />
      <ProfilePageLayout title="" actions={false} size="medium">
        {isWebView ? <InstantTransferTermsWebviewBackRow /> : null}
        <Box width="100%" maxWidth="100%">
          <InstantTransferTermsArticle>
            <InstantTransferTermsBody />
          </InstantTransferTermsArticle>
        </Box>
      </ProfilePageLayout>
    </>
  );
}
